/**
 * aerialThumb.ts — an aerial crop of a camp for rows that have no photo.
 *
 * Most listings carry no photo, and a column of identical caravan icons
 * reads as "no data". An aerial view of the actual spot is honest (it IS
 * the place) and every row looks different: a park's rows of vans, a rest
 * area's gravel loop beside the highway, bush around a camp area.
 *
 * Pure: picks the imagery tiles that cover a square window centred on the
 * camp and says where each tile sits inside it, as CSS background layers.
 * Tiles are fetched at zoom 16 and drawn at half size, so the crop is
 * sharp on a 2x screen and covers about 300 m across at 72 px.
 */

import type { CSSProperties } from 'react';

export const AERIAL_ZOOM = 16;
export const AERIAL_ATTRIBUTION = 'Imagery © Esri, Maxar, Earthstar Geographics';
const TILE = 256;
const SCALE = 0.5;

export const aerialTileUrl = (z: number, x: number, y: number) =>
  `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${z}/${y}/${x}`;

/** Web-Mercator pixel position of a point at zoom z (256px tiles). */
export function worldPixel(lat: number, lng: number, z: number): { x: number; y: number } {
  const n = TILE * 2 ** z;
  const s = Math.sin((Math.max(-85, Math.min(85, lat)) * Math.PI) / 180);
  return {
    x: ((lng + 180) / 360) * n,
    y: (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * n,
  };
}

export interface AerialLayer {
  url: string;
  /** Offset of the tile's top-left inside the window, display px. */
  left: number;
  top: number;
  /** Drawn size of one tile, display px. */
  size: number;
}

/**
 * The tiles that fill a `size`-px square centred on (lat, lng), with each
 * one's offset. Null for coordinates we can't place.
 */
export function aerialLayers(lat: number, lng: number, size: number, z = AERIAL_ZOOM): AerialLayer[] | null {
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || !(size > 0)) return null;
  const p = worldPixel(lat, lng, z);
  const half = size / SCALE / 2; // window half-width in source px
  const x0 = p.x - half;
  const y0 = p.y - half;
  const tx0 = Math.floor(x0 / TILE);
  const ty0 = Math.floor(y0 / TILE);
  const tx1 = Math.floor((p.x + half - 1e-6) / TILE);
  const ty1 = Math.floor((p.y + half - 1e-6) / TILE);
  const max = 2 ** z;
  const out: AerialLayer[] = [];
  for (let ty = ty0; ty <= ty1; ty++) {
    if (ty < 0 || ty >= max) continue;
    for (let tx = tx0; tx <= tx1; tx++) {
      const wx = ((tx % max) + max) % max;
      out.push({
        url: aerialTileUrl(z, wx, ty),
        left: Math.round((tx * TILE - x0) * SCALE * 100) / 100,
        top: Math.round((ty * TILE - y0) * SCALE * 100) / 100,
        size: TILE * SCALE,
      });
    }
  }
  return out.length ? out : null;
}

/** The layers as one CSS background (image, position, size). */
export function aerialBackground(layers: AerialLayer[]): CSSProperties {
  return {
    backgroundImage: layers.map((l) => `url("${l.url}")`).join(', '),
    backgroundPosition: layers.map((l) => `${l.left}px ${l.top}px`).join(', '),
    backgroundSize: layers.map((l) => `${l.size}px ${l.size}px`).join(', '),
    backgroundRepeat: 'no-repeat',
  };
}
